import { Document, Font, Page, Text, View } from '@react-pdf/renderer'
import { DefaultParamType, TFnType, TranslationKey } from '@tolgee/react'
import { FC } from 'react'
import { pdfStyles } from './../atoms/pdfStyles'
import { TableRow1 } from './../atoms/tableRow1'
import { TableRow2 } from './../atoms/tableRow2'
import { TableRow4 } from './../atoms/tableRow4'
import NotoSansBold from './../font/NotoSans-Bold.ttf'
import NotoSansRegular from './../font/NotoSans-Regular.ttf'

interface Props {
  data: EirData
  t: TFnType<DefaultParamType, string, TranslationKey>
}

export interface EirData {
  orderId: number
  orderNumber?: string
  licensePlateNumber: string
  trailerNumber?: string
  driver?: string
  passport?: string
  trustNumber?: string
  carrierVisitName?: string
  voyageNumber?: string
  shippingLine?: string
  dateOfIssue?: string
  imoClass?: string
  unNumber?: string
  notes?: string
  company?: string
  gateInDate: string
  gateInTime: string
  consigneeAddress?: string
  consignee?: string
  truckCompany?: string
  containerNumber?: string
  containerType?: string
  containerIsoCode?: string
  containerSize?: string
  containerAmount?: string
  containerContent?: string
  containerGrossWeight?: string
  containerTareWeight?: string
  containerNetWeight?: string
  containerSeals?: string
  forwarder?: string
  portOfDischarge?: string
  isEmpty?: boolean
  direction?: string
  isDamaged?: boolean
  damageDescription?: string[]
}

const Header: FC<Props> = props => {
  const { data, t } = props

  return (
    <>
      <Text style={pdfStyles.title}>{data.company}</Text>

      {data.direction === 'Inbound' ? (
        <Text style={pdfStyles.subtitle}>
          {t('eirReceival', 'Equipment Interchange Receipt: Receival')}
        </Text>
      ) : (
        <Text style={pdfStyles.subtitle}>
          {t('eirReceival', 'Equipment Interchange Receipt: Delivery')}
        </Text>
      )}

      <Text style={pdfStyles.subtitle}>{data.containerNumber}</Text>

      <Text style={pdfStyles.date}>
        Date: {data.gateInDate} Time: {data.gateInTime}
      </Text>
    </>
  )
}

const UpperSection: FC<Props> = ({ data, t }) => (
  <>
    <TableRow1 col1={t('consignee', 'Consignee')} col2={data.consignee} />
    <TableRow1 col1={t('consigneeAddress', 'Consignee address')} col2={data.consigneeAddress} />
    <TableRow1 col1={t('transportCompany', 'Transport company')} col2={data.truckCompany} />
    <TableRow1 col1={t('licensePlateNo', 'License plate no.')} col2={data.licensePlateNumber} />
    <TableRow2
      col1={t('driver', 'Driver')}
      col2={data.driver}
      col3={t('passport', 'Passport')}
      col4={data.passport}
    />
    <TableRow2
      col1={t('carrierVisitName', 'Carrier visit name')}
      col2={data.carrierVisitName}
      col3={
        data.direction === 'Inbound'
          ? t('importVoyageNo', 'Import voyage no.')
          : t('exportVoyageNo', 'Export voyage no.')
      }
      col4={data.voyageNumber}
    />
    {data.forwarder && <TableRow1 col1={t('forwarder', 'Forwarder')} col2={data.forwarder} />}
    {data.portOfDischarge && (
      <TableRow1 col1={t('portOfDischarge', 'Port of discharge')} col2={data.portOfDischarge} />
    )}
    <TableRow1 col1={t('shippingLine', 'Shipping line')} col2={data.shippingLine} />
  </>
)

const ContainerTable: FC<Props> = ({ data, t }) => (
  <View>
    <View style={pdfStyles.tableRow}>
      <Text style={{ ...pdfStyles.tableHeader, width: '15%' }}>
        {t('containerNo', 'Container no.')}
      </Text>
      <Text style={{ ...pdfStyles.tableHeader, width: '10%' }}>{t('archetype', 'Archetype')}</Text>
      <Text style={{ ...pdfStyles.tableHeader, width: '10%' }}>{t('isoCode', 'ISO Code')}</Text>
      <Text style={{ ...pdfStyles.tableHeader, width: '12%' }}>
        {t('fullOrEmpty', 'Full/Empty')}
      </Text>
      <Text style={{ ...pdfStyles.tableHeader, width: '10%' }}>
        {t('containerGrossWeight', 'Gross weight')}
      </Text>
      <Text style={{ ...pdfStyles.tableHeader, width: '10%' }}>
        {t('containerNetWeight', 'Net weight')}
      </Text>
      <Text style={{ ...pdfStyles.tableHeader, width: '10%' }}>
        {t('containerTareWeight', 'Tare weight')}
      </Text>
      <Text style={{ ...pdfStyles.tableHeader, width: '12%' }}>
        {t('containerContent', 'Content')}
      </Text>
      <Text style={{ ...pdfStyles.tableHeader, width: '8%' }}>{t('seals', 'Seals')}</Text>
      <Text style={{ ...pdfStyles.tableHeader, width: '15%' }}>{t('sealNumber', 'Seal No')}</Text>
    </View>
    <View style={pdfStyles.tableRow}>
      <Text style={{ ...pdfStyles.tableColumn, width: '15%' }}>{data.containerNumber}</Text>
      <Text style={{ ...pdfStyles.tableColumn, width: '10%' }}>{data.containerType}</Text>
      <Text style={{ ...pdfStyles.tableColumn, width: '10%' }}>{data.containerIsoCode}</Text>
      <Text style={{ ...pdfStyles.tableColumn, width: '12%' }}>
        {data.isEmpty ? 'Empty' : 'Full'}
      </Text>
      <Text style={{ ...pdfStyles.tableColumn, width: '10%' }}>{data.containerGrossWeight}</Text>
      <Text style={{ ...pdfStyles.tableColumn, width: '10%' }}>{data.containerNetWeight}</Text>
      <Text style={{ ...pdfStyles.tableColumn, width: '10%' }}>{data.containerTareWeight}</Text>
      <Text style={{ ...pdfStyles.tableColumn, width: '12%' }}>{data.containerContent}</Text>
      <Text style={{ ...pdfStyles.tableColumn, width: '8%' }}>
        {data.containerSeals && data.containerSeals.length > 0 ? 'Yes' : 'No'}
      </Text>
      <Text style={{ ...pdfStyles.tableColumn, width: '15%' }}>{data.containerSeals}</Text>
    </View>
  </View>
)

const LowerSection: FC<Props> = ({ data, t }) => (
  <>
    {data.isDamaged && (
      <TableRow1 col1={t('damages', 'Damages')} col2={data.isDamaged ? 'Yes' : 'No'} />
    )}
    {data.imoClass && (
      <TableRow2
        col1={t('imoClass', 'IMO class')}
        col2={data.imoClass}
        col3={data.unNumber ? t('unNumber', 'UN number') : ''}
        col4={data.unNumber || ''}
      />
    )}
    <TableRow4 col1={t('commentsAndNotes', 'Comments/notes')} col2={data.notes} />
  </>
)

const Footer: FC<Props> = props => {
  const { t, data } = props

  return (
    <>
      <View style={pdfStyles.footer}>
        <View style={{ textAlign: 'center', fontWeight: 'bold' }}>
          <Text>
            {t(
              'eirFooterTitle',
              'By signing this "Equipment Interchange Receipt" I am certifying that I have inspected and received the container with the above number.',
            )}
          </Text>
        </View>
        <View style={{ textAlign: 'justify', marginTop: 10 }}>
          <Text>
            <Text>
              {`This document accurately reflects the condition and transfer of the above container by ${data.truckCompany}. It confirms that there are no claims against the terminal regarding the container's condition, cargo, or comments.`}
            </Text>
          </Text>
        </View>
        <View style={{ display: 'flex', flexDirection: 'row', marginTop: 10, gap: 10 }}>
          <View style={{ display: 'flex', flexDirection: 'row', width: '40%' }}>
            <Text style={{ fontSize: 8, width: '20%' }}>{t('eirSignature', 'Signature')}</Text>
            <Text style={{ width: '80%', borderBottom: '1 solid #000' }} />
          </View>
          <View style={{ display: 'flex', flexDirection: 'column', width: '60%' }}>
            <Text style={{ width: '100%', borderBottom: '1 solid #000', fontSize: 8 }}>
              {t('eirIAm', 'I am')}
            </Text>
            <Text style={{ fontSize: 8 }}>
              {t(
                'eirAcceptance',
                'I consent to the processing of my personal data (name & passport data)',
              )}
            </Text>
          </View>
        </View>
        <View style={{ display: 'flex', flexDirection: 'row', marginTop: 20, gap: 10 }}>
          <View style={{ display: 'flex', flexDirection: 'row' }}>
            <Text style={{ fontSize: 8, width: '20%' }}>
              {t('eirCompanyDirector', 'Company director')}
            </Text>
            <Text style={{ width: '80%', borderBottom: '1 solid #000' }} />
          </View>
        </View>
      </View>
    </>
  )
}

export const EquipmentInterchangeReceipt: FC<Props> = props => {
  // Registering a custom font to support Cyrillic characters.
  // The default PDF fonts provided by the library do not have glyphs for these alphabets.

  Font.register({
    family: 'NotoSans',
    src: NotoSansRegular,
  })

  Font.register({
    family: 'NotoSans',
    src: NotoSansBold,
    fontWeight: 'bold',
  })

  return (
    <Document>
      <Page size='A4' style={pdfStyles.page}>
        <Header {...props} />
        <View style={pdfStyles.table}>
          <UpperSection {...props} />
          <ContainerTable {...props} />
          <LowerSection {...props} />
        </View>
        <Footer {...props} />
      </Page>
    </Document>
  )
}
